// brushSample — 選択中ペンの「実際の描き味」を小さなプレビューに描く純関数
// (consult 2026-06-08 / Modern ツールプロパティの筆跡プレビュー).
//
// ## なぜ drawing-core に置くか
//
// プレビューは **本物の stroke レンダラ** で「～」(緩いサイン波) を描く. 素朴な
// canvas2d `lineTo` 近似ではなく、`StrokeRenderer` + rasterizer (`createLayer` /
// `createStrokeBuffer` / `drawDot` / `drawLine` / `commitBufferToLayer` /
// `perStampAlphaFor`) と同じ描画コアを通すので、サーバ snapshot とも bit-perfect
// に一致する筆跡が出る (= 独自エンジン訴求 / architecture.md §11 / §19.1). web 専用に
// する理由がない (DOM 非依存の pure typed-array 演算のみ) ため、共有 package に置く.
//
// ## 反映される描き味
//
//   - ペン種: `'brush'` (累乗カーブ build-up、重なりで濃度蓄積) と `'flat'`
//     (uniform alpha) / `'marker'` の commit cadence・蓄積モード差がそのまま出る.
//     `perStampAlphaFor` / `accumulate` の分岐は本番 stroke と同一経路.
//   - 太さ (width) / 不透明度 (alpha 0..255).
//   - **筆圧の入り抜き**: 固定の圧力プロファイル (端で細く/薄く → 中央で太く/濃く)
//     を「～」点列に与え、現在の `PenPressureSettings` (widthEnabled/alphaEnabled/
//     min) を適用する. 太さ筆圧 ON なら端が細く、濃さ筆圧 ON なら端が薄くなる.
//
// ## 色は出さない (背景=白 / 筆跡=黒固定、consult 2026-06-08)
//
// サンプルは「色」でなく「ブラシの描き味 (形・build-up・入り抜き)」を見せる役割に割り切る
// (色はカラーパネル + 現在色スウォッチで分かる). よって:
//   - 背景を **白** (`CANVAS_BG_RGB`) で塗ったうえに筆跡を載せる (= 暗いパネル背景に黒
//     インクが埋もれない). stroke は透明レイヤーに描いてから白地へ source-over 合成する.
//   - 筆跡色は **黒 (`0x000000`) 固定**. 現在のペン描画色 (rgb24) は反映しない.
//   - alpha は反映するので、低 alpha のブラシは白地に薄いグレーの「～」になる (= 意図どおり).
//
// マスクはサンプルに反映しない (= 描き味と無関係、`mask: { type: 'none' }` 固定).
// 消しゴムはサンプル不要なので呼び出し側で出し分ける (この関数は描画ペン専用).
//
// ## 同期で完結
//
// worker / OffscreenCanvas に依存せず、その場で `Layer` を 1 枚確保して stroke を
// 流し切り、`layer.main` (= `ImageData.data` 互換の RGBA Uint8ClampedArray) を返す.
// プレビューは軽量 (~数百 px × ~40px) なので毎回の再生成で十分.
import { CANVAS_BG_RGB, createLayer } from './rasterizer.js';
import { StrokeRenderer } from './strokeRenderer.js';
/** 筆跡インク色 (黒固定、RGB24)。現在のペン色は反映しない (consult 2026-06-08). */
const INK_RGB = 0x000000;
/**
 * サンプル「～」の点数。波 1.5 周期を ~数十点でサンプルする. 多すぎると遅く、
 * 少なすぎるとカクつくが、本番 stroke も `drawLine` の sweep で点間を補間するので
 * この点数は「圧力プロファイルの解像度」を決めるだけ (= 線自体は連続).
 */
const SAMPLE_POINT_COUNT = 48;
/**
 * 波の周期数 (「～」らしい緩い波). 0.75 周期 ≈ 単一の緩い山 1 つ (= 「N」字くらい).
 * 1.5 だと山が複数出て「M」字に見えるので減らした (consult 2026-06-08).
 */
const SAMPLE_WAVE_CYCLES = 0.75;
/** 端の圧力下限 (= 入り抜きの「入り」「抜き」の細さ/薄さ). */
const SAMPLE_PRESSURE_EDGE = 0.2;
/** 線形筆圧 mapping (drawing-core `PRESSURE_GAMMA` が全ペン全軸 gamma=1 のため線形). */
const applyMin = (enabled, min, p) => {
    if (!enabled)
        return 1.0;
    const m = Math.max(0, Math.min(1, min));
    const pp = Math.max(0, Math.min(1, p));
    return m + (1 - m) * pp;
};
/**
 * 固定の圧力プロファイル: 端 `SAMPLE_PRESSURE_EDGE` → 中央 1.0 の滑らかな山.
 * `cos` ベースのベル形 (t∈[0,1])。両端で edge、t=0.5 で 1.0.
 */
const pressureProfile = (t) => {
    // 0.5*(1 - cos(2πt)) は t=0,1 で 0、t=0.5 で 1 のベル. それを edge..1 に写す.
    const bell = 0.5 * (1 - Math.cos(2 * Math.PI * t));
    return SAMPLE_PRESSURE_EDGE + (1 - SAMPLE_PRESSURE_EDGE) * bell;
};
/**
 * 選択中ペンの描き味で「～」(緩いサイン波 + 入り抜き圧力) を 1 ストローク描き、
 * RGBA を返す純関数. 同入力 → 決定論的 (乱数なし).
 *
 * 本番 stroke と同じ `StrokeRenderer` を 1 ストローク分だけ駆動する:
 *   1. `createLayer` で透明レイヤーを 1 枚確保
 *   2. `startStroke` (固定 sid、mask none、色は黒固定)
 *   3. 「～」点列を `addPoints` に流す (各点に圧力プロファイル × 筆圧設定 mapping を
 *      `p` / `pAlpha` として乗せる → rasterizer が widthScale / alphaScale に反映)
 *   4. `endStroke` (= `'flat'` / `'marker'` の deferred commit もここで確定)
 *   5. 白地に source-over 合成して返す (= 背景白 / 黒筆跡、低 alpha は薄いグレー)
 */
export const renderBrushSample = (params) => {
    const { width, height, pen, penWidth, alpha, pressure, penParams } = params;
    const w = Math.max(1, Math.floor(width));
    const h = Math.max(1, Math.floor(height));
    const layer = createLayer(0, w, h);
    const renderer = new StrokeRenderer([layer]);
    // 消しゴムは描き味プレビュー対象外 (呼び出し側で出し分ける)。万一渡されても
    // 筆跡なしの白地を返す (= 落とさない).
    if (pen === 'eraser') {
        return { rgba: compositeOverWhite(layer.main, w, h), width: w, height: h };
    }
    // 「～」を canvas にフィットさせる. 左右に太さ分のマージンを取って端が切れない
    // ようにする. 縦は中央基準に振幅を取り、上下も太さ分のマージンを確保.
    const margin = Math.max(2, penWidth * 0.6);
    const x0 = margin;
    const x1 = Math.max(x0 + 1, w - margin);
    const cy = h / 2;
    // 振幅: 上下マージンを引いた残りの半分. ペンが太いほど波を控えめに.
    const amp = Math.max(0, h / 2 - margin);
    renderer.startStroke({
        sid: 'brush-sample',
        pen,
        color: INK_RGB,
        alpha,
        width: penWidth,
        layerId: 0,
        mask: { type: 'none', colors: [] },
        penParams,
        nowMs: 0,
    });
    for (let i = 0; i < SAMPLE_POINT_COUNT; i++) {
        // SAMPLE_POINT_COUNT は定数 (> 1) なので 0 除算は起きない.
        const t = i / (SAMPLE_POINT_COUNT - 1);
        const x = x0 + (x1 - x0) * t;
        const y = cy - amp * Math.sin(t * SAMPLE_WAVE_CYCLES * 2 * Math.PI);
        const profile = pressureProfile(t);
        const p = pressure ? applyMin(pressure.widthEnabled, pressure.widthMin, profile) : 1.0;
        const pAlpha = pressure ? applyMin(pressure.alphaEnabled, pressure.alphaMin, profile) : 1.0;
        renderer.addPoints('brush-sample', [{ x, y, p, pAlpha }], 0);
    }
    renderer.endStroke('brush-sample');
    return { rgba: compositeOverWhite(layer.main, w, h), width: w, height: h };
};
/**
 * 透明レイヤー (黒筆跡 + α) を **不透明な白地** に source-over 合成した RGBA を返す.
 * 筆跡が黒 (RGB=0) なので合成後の色は `255 * (1 - a)` = α に応じた白〜黒のグレー
 * (= 低 alpha のブラシは薄いグレーの「～」). 出力 α は全 px 255 (= 不透明).
 */
const compositeOverWhite = (src, w, h) => {
    const bgR = (CANVAS_BG_RGB >> 16) & 0xff;
    const bgG = (CANVAS_BG_RGB >> 8) & 0xff;
    const bgB = CANVAS_BG_RGB & 0xff;
    const out = new Uint8ClampedArray(w * h * 4);
    for (let i = 0; i < out.length; i += 4) {
        const a = src[i + 3] ?? 0;
        if (a === 0) {
            // 空セル: 背景白そのまま.
            out[i] = bgR;
            out[i + 1] = bgG;
            out[i + 2] = bgB;
            out[i + 3] = 255;
            continue;
        }
        // source-over: out = src*a + bg*(255-a), 0..255 正規化.
        const sr = src[i] ?? 0;
        const sg = src[i + 1] ?? 0;
        const sb = src[i + 2] ?? 0;
        const inv = 255 - a;
        out[i] = (sr * a + bgR * inv) / 255;
        out[i + 1] = (sg * a + bgG * inv) / 255;
        out[i + 2] = (sb * a + bgB * inv) / 255;
        out[i + 3] = 255;
    }
    return out;
};
//# sourceMappingURL=brushSample.js.map